import { AppSelector } from './../../../../core/state/app-selector';
import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core';
import { ReportsService } from '../../../core/reports.service';
import { empty, Subscription } from 'rxjs';
import { tap, catchError, take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { PermissionsService } from '../../../../core/permissions.service';
import { ReportOptionsService } from '../report-options.service';

@Component({
  selector: 'app-vehicles-multi-selector',
  templateUrl: './vehicles-multi-selector.component.html',
  styleUrls: ['./vehicles-multi-selector.component.scss'],
})
export class VehiclesMultiSelectorComponent implements OnInit, OnDestroy {
  vehicles: any;
  vehiclesOptions = [];
  selectedVehicles: any = [];
  vehiclesIsLoading = true;
  groupSubscription: Subscription;
  typeSubscription: Subscription;

  @Input() reportId: number;
  @Input() type: string;
  @Input() defaultLabel: string = this.translateService.instant(
    'reports.select-vehicles'
  );
  @Input() selectedItemsLabel: string =
    '{0} ' + this.translateService.instant('reports.are-selected-vehicles');
  @Input() label = this.translateService.instant('reports.vehicle');
  @Input() selectedVehiclesIds: any[] = [];
  @Input() selectAllVehicles: boolean = false;
  @Input() dropdownClass: string = 'multiselect--gray';
  @Input() dropdownIconClass: string = 'fa fa-chevron-down';
  @Output() selectedVehiclesIdsChange = new EventEmitter<Array<any>>();

  constructor(
    private reportsService: ReportsService,
    private translateService: TranslateService,
    public permissionsService: PermissionsService,
    private reportOptionsService: ReportOptionsService,
    private appSelector: AppSelector
  ) {}

  ngOnInit() {
    this.loadSelectOptions();
  }

  ngOnDestroy() {
    if (this.groupSubscription) this.groupSubscription.unsubscribe();
    if (this.typeSubscription) this.typeSubscription.unsubscribe();
  }

  private onVehicleGroupChange(): (value: any) => void {
    return (res) => {
      this.prepareGroupFilter(res);
      this.filterAndSelectVehicle();
    };
  }

  private onFilterTypeChange(): (value: any) => void {
    return (res) => {
      this.prepareTypeFilter(res);
      this.filterAndSelectVehicle();
    };
  }

  private prepareGroupFilter(res: any) {
    if (typeof res === 'object') {
      this.reportsService.filterByGroupIsEnable = true;
      this.reportsService.filterGroupIdArray = res;
    } else {
      this.reportsService.filterByGroupIsEnable = false;
    }
  }

  private prepareTypeFilter(res: any) {
    if (res > 0) {
      this.reportsService.filterByTypeIsEnable = true;
      this.reportsService.filterTypeId = res;
    } else {
      this.reportsService.filterByTypeIsEnable = false;
    }
  }

  private filterAndSelectVehicle() {
    this.vehiclesIsLoading = true;
    this.vehiclesOptions = this.filterVehiclesList();
    this.selectedVehiclesIds = this.vehiclesOptions.map(
      (option) => option.value
    );
    if (this.selectAllVehicles) {
      this.selectedVehicles = this.vehiclesOptions;
    } else {
      this.selectedVehicles = this.selectedVehicles.filter((vehicle) =>
        this.selectedVehiclesIds.includes(vehicle.value)
      );
    }
    this.selectedVehiclesIdsChange.emit(this.selectedVehicles);
    this.vehiclesIsLoading = false;
  }

  private filterByType(vehicle: any): boolean {
    return vehicle.typeId === this.reportsService.filterTypeId;
  }

  private filterByGroup(vehicle: any): boolean {
    return this.reportsService.filterGroupIdArray.indexOf(vehicle.id) !== -1;
  }

  private filterVehiclesList(): any[] {
    return this.vehicles
      .filter(
        (vehicle) =>
          !this.reportsService.filterByGroupIsEnable ||
          this.filterByGroup(vehicle)
      )
      .filter(
        (vehicle) =>
          !this.reportsService.filterByTypeIsEnable ||
          this.filterByType(vehicle)
      )
      .map((vehicle) => {
        return { label: vehicle.description, value: vehicle.id };
      });
  }

  loadSelectOptions() {
    this.vehiclesIsLoading = true;
    this.vehiclesOptions = [];
    this.appSelector
      .getSortedPositions()
      .pipe(take(1))
      .subscribe((positions) => {
        this.reportsService
          .getVehicles(this.reportId)
          .pipe(
            tap((data: any[]) => {
              this.vehicles = positions
                .map((position) => {
                  return {
                    ...data.find((el) => el.id == position.vehicle),
                  };
                })
                .filter((position) => position.typeId != undefined);
              this.vehiclesOptions = this.vehicles.map((vehicle) => {
                return { label: vehicle.description, value: vehicle.id };
              });
              this.groupSubscription = this.reportsService.filterByVehicleGroupId.subscribe(
                this.onVehicleGroupChange()
              );
              this.typeSubscription = this.reportsService.filterByVehicleTypeId.subscribe(
                this.onFilterTypeChange()
              );
              this.selectedVehicles = [];
              if (this.selectAllVehicles) {
                this.selectedVehicles = this.vehiclesOptions;
              } else if (
                this.selectedVehiclesIds != null &&
                this.selectedVehiclesIds.length > 0
              ) {
                this.selectedVehicles = this.vehiclesOptions.filter((option) =>
                  this.selectedVehiclesIds.includes(option.value)
                );
              }
              this.selectedVehiclesIdsChange.emit(this.selectedVehicles);
              this.vehiclesIsLoading = false;
            }),
            catchError((error, caught) => {
              this.vehiclesOptions = [];
              this.vehicles = [];
              this.selectedVehicles = [];
              this.selectedVehiclesIdsChange.emit([]);
              this.vehiclesIsLoading = false;
              return empty();
            })
          )
          .subscribe();
      });
  }

  onSelectedVehiclesIdChange() {
    const vehiclesIds = [];
    if (this.selectedVehicles) {
      if (this.selectedVehicles.length > 0) {
        this.selectedVehicles.forEach((element) => {
          vehiclesIds.push(element.value);
        });
        this.selectedVehiclesIdsChange.emit(this.selectedVehicles);
        this.reportOptionsService.vehicleName = this.selectedVehicles[0].label;
        // this.permissionsService
        //   .getArchivePermissions(this.reportId, vehiclesIds, -1)
        //   .subscribe((res) => {
        //     this.permissionsService.setArchivePermission.next(res);
        //   });
      } else {
        this.selectedVehiclesIdsChange.emit([]);
        this.reportOptionsService.vehicleName = '';
        // this.permissionsService
        //   .getArchivePermissions([this.reportId], vehiclesIds, -1)
        //   .subscribe((res) => {
        //     this.permissionsService.setArchivePermission.next(res);
        //   });
      }
    } else {
      this.selectedVehiclesIdsChange.emit([]);
      this.reportOptionsService.vehicleName = '';
      // this.permissionsService
      //   .getArchivePermissions([this.reportId], vehiclesIds, -1)
      //   .subscribe((res) => {
      //     this.permissionsService.setArchivePermission.next(res);
      //   });
    }
  }
}
